package co.uk.paxes.pinpoint.web.ads

import co.uk.paxes.pinpoint.web.platform.PlatformDetector
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.JsExport
import kotlin.js.JsName

/**
 * Manages banner ad display on static pages.
 * 
 * Injects banner ads into how-to-play.html and strategy-tips.html pages
 * when running on Facebook Instant Games platform.
 */
@JsExport
object BannerAdManager {
    
    /**
     * Initialize banners for the current page.
     * Detects which page is loaded and injects appropriate banner ads.
     */
    @JsName("initializeBannersForCurrentPage")
    fun initializeBannersForCurrentPage() {
        if (!PlatformDetector.isFacebook()) {
            console.log("ℹ️ BannerAdManager: Skipping banner initialization (standalone mode)")
            return
        }
        
        val currentPath = window.location.pathname
        console.log("📰 BannerAdManager: Initializing banners for path: $currentPath")
        
        when {
            currentPath.contains("how-to-play.html") -> {
                console.log("📰 BannerAdManager: Detected how-to-play page")
                injectBanner("banner-container-howtoplay", "banner_howtoplay")
            }
            currentPath.contains("strategy-tips.html") -> {
                console.log("📰 BannerAdManager: Detected strategy-tips page")
                injectBanner("banner-container-strategy", "banner_strategy")
            }
            else -> {
                console.log("ℹ️ BannerAdManager: No banner configuration for this page")
            }
        }
    }
    
    /**
     * Inject a banner ad into the specified container.
     * 
     * @param containerId The ID of the HTML element to inject the banner into
     * @param placementID The Facebook ad placement ID
     */
    private fun injectBanner(containerId: String, placementID: String) {
        console.log("📰 BannerAdManager: Injecting banner into $containerId with placement $placementID")
        
        val container = document.getElementById(containerId)
        if (container == null) {
            console.warn("⚠️ BannerAdManager: Container $containerId not found")
            return
        }
        
        // Note: Facebook Instant Games doesn't support traditional banner ads
        // Banner ads are typically only available through Audience Network for mobile apps
        // For web-based Instant Games, we'll show a placeholder or skip
        console.warn("⚠️ BannerAdManager: Banner ads not supported in Facebook Instant Games web platform")
        console.warn("   Banner ads are only available through Audience Network in native mobile apps")
        
        // Show a message in the container
        container.innerHTML = """
            <div style="padding: 20px; text-align: center; background: #f0f0f0; border-radius: 8px; margin: 20px 0;">
                <p style="color: #666; margin: 0;">Ad space reserved for Facebook Audience Network</p>
            </div>
        """.trimIndent()
    }
}
